import { useCallback } from 'react'

import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormState,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'
import { produce } from 'immer'

import { fitMap, updateLocationPin } from '@/map'

import { LocationEditor as LocationEditorBase } from './LocationEditorBase'
import { PositionAdjusted } from './components/PositionAdjusted/PositionAdjusted'
import { useDepotsAsMap } from './hooks/useDepotsAsMap'
import { useLocationEditorField } from './hooks/useLocationEditorField'
import { useLocationEditorModal } from './modals/LocationEditorModal'
import { resolveConnectedDepotId } from './resolveConnectedDepotId'
import { LocationEditorFieldProps, LocationEditorFieldValue } from './types'

const defaultComponentValue: LocationEditorFieldValue = {
  address: '',
  geoAddress: '',
  latLng: { lat: 0, lng: 0 },
  rawLatLngAsString: '',
  source: 'MANUAL',
  status: 'OK',
}

export function LocationEditorField<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = LocationEditorFieldValue,
>(props: LocationEditorFieldProps<TargetField, State, AcceptedFormValue>) {
  const {
    locationEditorProps = {},
    initialValue = defaultComponentValue as FormFieldDescriptor<State>[TargetField]['value'],
    validateOn = 'change',
    debounce = null,
    placeholder,
    ...bridgeOptions
  } = props
  const { pinId, pinType } = props

  const { slotProps = {}, ...restOfLocationEditorProps } = locationEditorProps

  const {
    config: { label, testId = 'locationEditor-root' },
    fieldInfo: {
      errorOrHelperText,
      field: { visible },
      fieldDisabled,
      fieldHasError,
      onBlur,
      onChange,
      onFocus,
      showErrorAccent,
      value,
    },
  } = useTidalFormBridge<LocationEditorFieldValue, TargetField, State, AcceptedFormValue>({
    ...bridgeOptions,
    debounce,
    validateOn,
    initialValue,
  })

  const locationEditorFieldProps = useLocationEditorField(props, value, onChange)
  const { show: onShowAdjustAddressModal, Modal } = useLocationEditorModal(pinId)

  const depotsAsMap = useDepotsAsMap()
  const { onConnectedPlaceChanged, connectedPlaceId } = props

  const onChangeModal = useCallback(
    (location: uui.domain.client.Location) => {
      // Update Pin location
      updateLocationPin(pinId, prevPin =>
        produce(prevPin, draft => {
          draft.pin.location = location
          draft.pin.latLng = location.latLng
        }),
      )

      // Adjusting the address is a user-driven location move, so re-derive the
      // connected depot from the new coordinates (same rule as dragging the pin).
      // The pin subscription skips this path because it is not a loading move.
      if (onConnectedPlaceChanged) {
        const nextConnectedPlaceId = resolveConnectedDepotId(
          depotsAsMap,
          location.rawLatLngAsString,
          connectedPlaceId,
        )
        if (connectedPlaceId !== nextConnectedPlaceId) {
          onConnectedPlaceChanged(nextConnectedPlaceId)
        }
      }

      // Fit map to new location
      fitMap([location.latLng])
    },
    [pinId, onConnectedPlaceChanged, connectedPlaceId, depotsAsMap],
  )

  if (!visible) return null

  const positionAdjusted = value.address !== value.geoAddress

  return (
    <div>
      <LocationEditorBase
        data-tidal-form-field-name={bridgeOptions.name}
        label={label}
        slotProps={{
          ...slotProps,
          textfield: {
            onBlur,
            onFocus,
            ...slotProps.textfield,
          },
        }}
        testId={testId}
        disabled={fieldDisabled}
        error={showErrorAccent && fieldHasError}
        helperText={errorOrHelperText}
        placeholder={placeholder}
        location={value}
        onShowAdjustAddressModal={onShowAdjustAddressModal}
        {...locationEditorFieldProps}
        {...restOfLocationEditorProps}
      />

      {positionAdjusted && <PositionAdjusted />}

      <Modal
        label={value.address}
        pinId={pinId}
        pinType={pinType}
        onChange={onChangeModal}
        location={value}
      />
    </div>
  )
}
